import React from "react";
import PropTypes from "prop-types";
import { Form, Button, Icon, Card, Tabs, Drawer, Upload, Select, Modal, message } from "antd";
import { Table } from "components";
import { onCellGenerator } from "constants/table-columns";
import { OrderedMap, Map, List } from "immutable";
import cx from "classnames";
import Clipboard from "assets/clipboard.png";
import {
    fieldGenerator,
    getErrorOnField,
    getValidateStatus
} from "utilities/form";
import shortID from "uuid/v4";
import {
    INPUT_TYPES, COMPANY_TYPES,
    GENDER_TYPES,
    NATURE_OF_ASSOCIATION_TYPES,
    BANK_ACCOUNT_TYPES,
    CORPORATION_TYPE
} from "constants/index";
import { APPLICANT_TYPES, BOOLEAN_OPTIONS } from "constants/enums";
import { CompanyService } from "services/index";
import { readSpreadsheet } from "utilities/excel-utils";
import "styles/tabs.css";
import CompanyLosService from "../services/company-los-services";

const { TabPane } = Tabs;
const { Option } = Select;

const getFormFields = ({
    gstinValidator,
    getChannelProducts,
    salesManagers,
    drmManagers,
    allFieldsDisabled,
    // gstinDisabled,
    formatSellerData,
    fetchIndustryTypes,
    vpSalesManagers
}) =>
    new OrderedMap({
        gstin: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "GSTIN",
            rules: [
                {
                    required: true,
                    messsage: "Required"
                },
                gstinValidator
            ],
            disabled: true,
            category: 1
        },
        name: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Name",
            rules: [
                {
                    required: true,
                    messsage: "Required"
                }
            ],
            disabled: true,
            category: 1
        },
        type: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Type",
            options: COMPANY_TYPES.map((value, label) => ({
                label,
                value: String(value)
            }))
                .toList()
                .toArray(),
            rules: [
                {
                    required: true,
                    messsage: "Required"
                }
            ],
            disabled: allFieldsDisabled,
            category: 1
        },
        channel_products: {
            className: "form-field fb-30 mr-3perc",
            label: "Primary Brands",
            type: INPUT_TYPES.OBJECT_SELECT,
            getOptionsOnFocus: getChannelProducts,
            labelProp: "name|id",
            mode: "multiple",
            filterOption: true,
            disabled: allFieldsDisabled,
            category: 1
        },
        industry: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            showSearch: true,
            label: "Primary Industry",
            disabled: allFieldsDisabled,
            category: 1,
            labelProp: "name",
            filterOption: true,
            getOptionsOnFocus: fetchIndustryTypes,
            onSearch: fetchIndustryTypes
        },
        program_anchor: {
            label: "Primary Anchor",
            type: INPUT_TYPES.OBJECT_SELECT,
            className: "form-field fb-30 w-33 mr-3perc",
            disabled: allFieldsDisabled,
            category: 2,
            showSearch: true,
            labelProp: "name",
            formatData: formatSellerData,
            onSearch: (str) => CompanyService.getCompanyProfiles(COMPANY_TYPES.get("Anchor"), str)
        },
        mis_email_recipients: {
            className: "form-field fb-30 mr-3perc",
            label: "MIS Email Recipients",
            type: INPUT_TYPES.SELECT,
            mode: "tags",
            options: [],
            disabled: allFieldsDisabled,
            category: 1
        },
        primary_relationship_manager: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            labelProp: "full_name",
            showSearch: true,
            rules: [
                {
                    required: true,
                    message: "Field Required"
                }
            ],
            onSearch: drmManagers,
            getOptionsOnFocus: drmManagers,
            label: "Primary Relationship Manager",
            disabled: allFieldsDisabled,
            category: 1,
            filterOption: true

        },
        secondary_relationship_manager: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            labelProp: "full_name",
            mode: "multiple",
            getOptionsOnFocus: salesManagers,
            label: "Secondary Relationship Managers",
            disabled: allFieldsDisabled,
            category: 1,
            filterOption: true

        },
        vp_sales: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            labelProp: "full_name",
            // mode: "default",
            showSearch: true,
            onSearch: vpSalesManagers,
            getOptionsOnFocus: vpSalesManagers,
            label: "VP Sales",
            disabled: allFieldsDisabled,
            category: 1,
            filterOption: true

        },
        zonal_head: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Zonal Head",
            disabled: true,
            category: 1
        },
        parent_industry: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Parent Industry",
            disabled: true,
            category: 1
        },
        industry_head: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Industry Head",
            disabled: true,
            category: 1
        },
        is_corporate_agent: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Corporate Agent",
            placeholder: "Corporate Agent",
            disabled: allFieldsDisabled,
            options: BOOLEAN_OPTIONS.map((value, key) => ({
                value,
                label: key
            })).toList()
        },
        additional_product_applicable: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Addtional Product Applicable",
            options: new Map({
                Vendor_Finance:2,
                Term_Loan: 7,
                CEP: 8,
            }).map((value, label) => ({
                label: label.split("_").join(" "),
                value: value
            })).toList().toArray(),
            mode: "multiple",
            disabled: allFieldsDisabled,
            category: 1
        },
        corporation_type: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Corporation Type",
            options: CORPORATION_TYPE.map((value, label) => ({
                label,
                value: value
            }))
                .toList()
                .toArray(),
            // rules: [
            //     {
            //         required: true,
            //         message: "Corporation Type is Required"
            //     }
            // ],
            disabled: true,
            category: 1
        },
        db_tagging: {
            className: "form-field fb-30 mr-3perc",
            label: "DB Tag",
            type: INPUT_TYPES.TEXT,
            labelProp: "db_tagging",
            disabled: true,
            category: 1,
        },
    });

const getDistFormFields = ({
    gstinValidator,
    getChannelProducts,
    salesManagers,
    drmManagers,
    allFieldsDisabled,
    // gstinDisabled,
    // formatSellerData,
    fetchIndustryTypes,
    vpSalesManagers,
    formatIndustryData,
    formatProgramAnchorData
}) =>
    new OrderedMap({
        gstin: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "GSTIN",
            rules: [
                {
                    required: true,
                    messsage: "Required"
                },
                gstinValidator
            ],
            disabled: true,
            category: 1
        },
        name: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Name",
            rules: [
                {
                    required: true,
                    messsage: "Required"
                }
            ],
            disabled: true,
            category: 1
        },
        type: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Type",
            options: COMPANY_TYPES.map((value, label) => ({
                label,
                value: String(value)
            }))
                .toList()
                .toArray(),
            rules: [
                {
                    required: true,
                    messsage: "Required"
                }
            ],
            disabled: allFieldsDisabled,
            category: 1
        },
        channel_products: {
            className: "form-field fb-30 mr-3perc",
            label: "Primary Brands",
            type: INPUT_TYPES.OBJECT_SELECT,
            getOptionsOnFocus: getChannelProducts,
            labelProp: "name|id",
            mode: "multiple",
            filterOption: true,
            disabled: allFieldsDisabled,
            category: 1
        },
        all_industries: {
            className: "form-field fb-30 mr-3perc",
            label: "All Industries",
            type: INPUT_TYPES.OBJECT_SELECT,
            getOptionsOnFocus: fetchIndustryTypes,
            labelProp: "name",
            mode: "multiple",
            filterOption: true,
            disabled: allFieldsDisabled,
            category: 1,
            showSearch: true,
            onSearch: fetchIndustryTypes
        },
        all_anchors: {
            label: "All Anchors",
            type: INPUT_TYPES.OBJECT_SELECT,
            className: "form-field fb-30 w-33 mr-3perc",
            disabled: allFieldsDisabled,
            category: 2,
            showSearch: true,
            mode: "multiple",
            labelProp: "name",
            // formatData: formatSellerData,
            // onSearch: (str) => CompanyService.getCompanyProfiles(COMPANY_TYPES.get("Anchor"), str)
        },
        industry: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            showSearch: true,
            label: "Primary Industry",
            disabled: allFieldsDisabled,
            category: 1,
            labelProp: "name",
            filterOption: true,
            getOptionsOnFocus: fetchIndustryTypes,
            formatData: formatIndustryData,
            onSearch: fetchIndustryTypes
        },
        program_anchor: {
            label: "Primary Anchor",
            type: INPUT_TYPES.OBJECT_SELECT,
            className: "form-field fb-30 w-33 mr-3perc",
            disabled: allFieldsDisabled,
            category: 2,
            showSearch: false,
            labelProp: "name",
            // formatData: formatSellerData,
            formatData: formatProgramAnchorData,
            getOptionsOnFocus: (str) => CompanyService.getCompanyProfiles(COMPANY_TYPES.get("Anchor"), str),
            onSearch: (str) => CompanyService.getCompanyProfiles(COMPANY_TYPES.get("Anchor"), str)
        },
        mis_email_recipients: {
            className: "form-field fb-30 mr-3perc",
            label: "MIS Email Recipients",
            type: INPUT_TYPES.SELECT,
            mode: "tags",
            options: [],
            disabled: allFieldsDisabled,
            category: 1
        },
        primary_relationship_manager: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            labelProp: "full_name",
            showSearch: true,
            rules: [
                {
                    required: true,
                    message: "Field Required"
                }
            ],
            onSearch: drmManagers,
            getOptionsOnFocus: drmManagers,
            label: "Primary Relationship Manager",
            disabled: allFieldsDisabled,
            category: 1,
            filterOption: true

        },
        secondary_relationship_manager: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            labelProp: "full_name",
            mode: "multiple",
            getOptionsOnFocus: salesManagers,
            label: "Secondary Relationship Managers",
            disabled: allFieldsDisabled,
            category: 1,
            filterOption: true

        },
        vp_sales: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "VP Sales",
            disabled: true,
            category: 1
        },
        zonal_head: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Zonal Head",
            disabled: true,
            category: 1
        },
        parent_industry: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Parent Industry",
            disabled: true,
            category: 1
        },
        industry_head: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            label: "Industry Head",
            disabled: true,
            category: 1
        },
        is_corporate_agent: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Corporate Agent",
            placeholder: "Corporate Agent",
            disabled: allFieldsDisabled,
            options: BOOLEAN_OPTIONS.map((value, key) => ({
                value,
                label: key
            })).toList()
        },
        distributor_service_manager: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.OBJECT_SELECT,
            labelProp: "full_name",
            label: "Distributor Service Managers",
            disabled: allFieldsDisabled,
            category: 1,
            filterOption: true
        },
        annual_turnover: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            labelProp: "annual_turnover",
            label: "Annual turnover INR Cr (Monthly Sales * 12)",
            disabled: allFieldsDisabled,
            category: 1,
            rules: [
                {
                    pattern: /^\d*\.?\d*$/,
                    message: "Value should contain just number",
                },
            ],
            filterOption: true
        },
        total_no_of_dealers: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.TEXT,
            labelProp: "total_no_of_dealers",
            label: "Total Number of Retailers",
            disabled: allFieldsDisabled,
            category: 1,
            rules: [
                {
                    pattern: /^(?:\d*)$/,
                    message: "Value should contain just number",
                },
            ],
            filterOption: true
        },
        additional_product_applicable: {
            className: "form-field fb-30 mr-3perc",
            type: INPUT_TYPES.SELECT,
            label: "Addtional Product Applicable",
            options: new Map({
                Vendor_Finance:2,
                Term_Loan: 7,
                CEP: 8,
            }).map((value, label) => ({
                label: label.split("_").join(" "),
                value: value
            })).toList().toArray(),
            mode: "multiple",
            disabled: allFieldsDisabled,
            category: 1
        },
        db_tagging: {
            className: "form-field fb-30 mr-3perc",
            label: "DB Tag",
            type: INPUT_TYPES.TEXT,
            labelProp: "db_tagging",
            disabled: true,
            category: 1,
        },
    });

const getUserDetails = (allFieldsDisabled) => new OrderedMap({
    first_name: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "First Name",
        disabled: allFieldsDisabled,
        category: 2
    },
    last_name: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Last Name",
        disabled: allFieldsDisabled.label,
        category: 2
    },
    email: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Email",
        disabled: allFieldsDisabled,
        category: 2
    },
    phone: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Mobile Number",
        disabled: allFieldsDisabled,
        category: 2
    },
    gender: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.SELECT,
        label: "Gender",
        options: GENDER_TYPES.map((value, label) => ({ value, label })).toList().toArray(),
        disabled: allFieldsDisabled,
        category: 2
    },
    type: {
        label: "Associate Type",
        placeholder: "Guarantor/Applicant",
        options: APPLICANT_TYPES.map((value, key) => ({
            value,
            label: key
        })).toList(),
        type: INPUT_TYPES.SELECT,
        className: "form-field fb-30 mr-3perc",
        category: 2,
        disabled: allFieldsDisabled
    },
    // "spoc_user.designation": {
    //     className: "form-field fb-30 mr-3perc",
    //     type: INPUT_TYPES.SELECT,
    //     label: "Designation",
    //     options: DESIGNATION_TYPES.map((value, label) => ({ value, label })).toList().toArray(),
    //     disabled: allFieldsDisabled
    // },
    nature_of_association: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.SELECT,
        label: "Relationship with Company",
        options: NATURE_OF_ASSOCIATION_TYPES.map((value, label) => ({
            label,
            value
        })).toList().toArray(),
        disabled: allFieldsDisabled,
        category: 2
    },
    pan: {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Pan",
        placeholder: "PAN",
        category: 2,
        disabled: allFieldsDisabled,
        rules: [
            {
                required: true,
                message: "PAN is required"
            },
            {
                max: 10,
                message: "Please Enter a valid PAN No"
            },
            {
                pattern: "[A-Z]{5}[0-9]{4}[A-Z]{1}",
                message: "Please Enter a valid PAN No"
            }
        ]
    },
    is_db_mou_triggered: {
        label: "Is DB MOU Triggered",
        // placeholder: "Select DB Mou",
        defaultValue: false,
        options: [{ label: "Yes", value: true }, { label: "No", value: false }],
        type: INPUT_TYPES.SELECT,
        className: "form-field fb-30 mr-3perc",
        category: 2,
        disabled: allFieldsDisabled,
        rules: [
            {
                required: true,
                message: "DB MOU is required"
            }
        ]
    },
    is_d2r_guarantor: {
        label: "Tag as Guarantor for D2R",
        type: INPUT_TYPES.RADIO,
        defaultValue: 'false',
        options: [{ label: "Yes", value: "true" }, { label: "No", value: "false" }],
        className: "form-field fb-30"
    }
});


const getBankFields = (allFieldsDisabled) => new OrderedMap({
    "bank_details.account_type": {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.SELECT,
        label: "Account Type",
        options: BANK_ACCOUNT_TYPES.map((value, label) => ({
            label,
            value
        })).toList().toArray(),
        disabled: true,
        defaultValue: 5,
        category: 3
    },
    "bank_details.account_holder_name": {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Account Holder Name",
        disabled: allFieldsDisabled,
        category: 3
    },
    "bank_details.account_number": {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.PASSWORD,
        label: "Account Number",
        autocomplete: "false",
        disabled: allFieldsDisabled,
        category: 3
    },
    "bank_details.re_enter_account_number": {
        className: "form-field fb-30 mr-3perc",
        type: INPUT_TYPES.TEXT,
        label: "Re-Enter Account Number",
        disabled: allFieldsDisabled,
        category: 3
    },
    "bank_details.ifsc": {
        type: INPUT_TYPES.OBJECT_SELECT,
        className: "form-field fb-30 mr-3perc",
        disabled: allFieldsDisabled,
        label: "Bank IFSC Code",
        showSearch: true,
        labelProp: "ifsc_code",
        category: 3
    },
    "bank_details.bank_branch": {
        type: INPUT_TYPES.OBJECT_TEXT,
        className: "form-field fb-30 mr-3perc",
        label: "Bank Branch",
        disabled: true,
        category: 3
    }
});

const RETAILER_WISE_MAPPING_COLS = new OrderedMap({
    dealer_name: {
        title: "Dealer Name",
        dataIndex: "dealer_name",
        key: "dealer_name",
    },
    dealer_gstin: {
        title: "Dealer GSTIN",
        dataIndex: "dealer_gstin",
        key: "dealer_gstin",
        editable: true,
        onCellGenerator
    },
    account_type: {
        title: "Account Type",
        dataIndex: "account_type",
        key: "account_type",
        render: _ => BANK_ACCOUNT_TYPES.findKey(__ => __ == _)
    },
    account_holder_name: {
        title: "Account Holder Name",
        dataIndex: "account_holder_name",
        key: "account_holder_name"
    },
    account_number: {
        title: "Account Number",
        dataIndex: "account_number",
        key: "account_number"
    },
    ifsc_code: {
        title: "Bank IFSC Code",
        dataIndex: "ifsc_code",
        key: "ifsc_code"
    }
});

const EDITABLE_RETAILER_WISE_MAPPING_COLS = new OrderedMap({
    dealer_gstin: {
        title: "Dealer GSTIN",
        dataIndex: "dealer_gstin",
        key: "dealer_gstin",
        editable: true,
        onCellGenerator
    },
    account_type: {
        title: "Account Type",
        dataIndex: "account_type",
        key: "account_type",
        editable: true,
        onCellGenerator,
        fieldType: INPUT_TYPES.SELECT,
        options: BANK_ACCOUNT_TYPES.map((value, label) => ({
            label,
            value
        })).toList().toArray(),
    },
    account_holder_name: {
        title: "Account Holder Name",
        dataIndex: "account_holder_name",
        key: "account_holder_name",
        editable: true,
        onCellGenerator
    },
    account_number: {
        title: "Account Number",
        dataIndex: "account_number",
        key: "account_number",
        editable: true,
        onCellGenerator
    },
    ifsc_code: {
        title: "Bank IFSC Code",
        dataIndex: "ifsc_code",
        key: "ifsc_code",
        editable: true,
        onCellGenerator
    }
});

class CompanyCreateForm extends React.Component {

    state = {
        userIds:
            this.props.spocValues && this.props.spocValues.size
                ? this.props.spocValues
                    .map(_ => _.id)
                    .mapEntries(([, v]) => [v, v])
                : new Map({}),
        userConfigs:
            this.props.spocValues && this.props.spocValues.size
                ? this.props.spocValues.toList()
                : new List([]),
        removed_users: [],
        activeTab: "default-account",
        mappingData: new List([]),
        newMappingData: new List([]),
        addSingle: false,
        showErrorTable: false,
        bankListData: null,
        bankRemoveConf: false,
        bankDeleteData: null,
        distributorManagersData: new List([]),
    };

    componentDidMount() {
        if(!this.state.userIds || !this.state.userIds.size) {
            this.toggleFormGrid("ADD");
        }
        CompanyLosService.getCompanyLosProfile(this.props.companyId).then(({data}) => {
            this.setState({
                bankListData: data.bank_accounts
            });
        }).catch((err) => {
            console.error(err);
        });
        CompanyLosService.getDSMList().then(({data})=> {
            this.setState({distributorManagersData: new List(data)});
        }).catch((err) => {
            console.error(err);
        });
    }

    componentDidUpdate(prevProps) {
        if(
            this.props.spocValues &&
            prevProps.spocValues !== this.props.spocValues
        ) {
            this.setState({
                userIds:
                    this.props.spocValues && this.props.spocValues.size
                        ? this.props.spocValues
                            .map(_ => _.id)
                            .mapEntries(([, v]) => [v, v])
                        : new Map({}),
                userConfigs:
                    this.props.spocValues && this.props.spocValues.size
                        ? this.props.spocValues.toList()
                        : new List([]),
            });
        }
        if(!this.state.userIds || !this.state.userIds.size) {
            this.toggleFormGrid("ADD");
        }
        if(prevProps.retailerMappingStatus === "FETCHING" && this.props.retailerMappingStatus === "FETCHED"){
            CompanyLosService.getCompanyLosProfile(this.props.companyId).then(({data}) => {
                this.setState({
                    bankListData: data.bank_accounts
                });
            }).catch((err) => {
                console.error(err);
            });
        }
        if(
            this.props.retailerMappingStatus && 
            this.props.retailerMappingStatus === "FETCHED" && 
            this.props.retailerMapping && 
            this.props.retailerMapping.length !== this.state.mappingData.size
        ) {
            let data = this.props.retailerMapping;
            if(data.length) {
                data = data.map(_ => {
                    return {
                        account_holder_name: _.account_holder_name,
                        account_number: _.account_number,
                        account_type: _.account_type,
                        ifsc_code: _.bank_branch && _.bank_branch.ifsc_code,
                        dealer_gstin: _.dealer && _.dealer.gstin,
                        dealer_name: _.dealer && _.dealer.name,
                        id: _.id
                    };
                });
            }
            this.setState({
                mappingData: new List(data),
                newMappingData: new List([])
            });
        }
        if(
            this.props.retailerMappingStatus && 
            this.props.retailerMappingStatus === "UPDATING" &&
            this.state.mappingData.size) {
            this.setState({
                mappingData: new List([])
            });
        }
        if(
            this.props.retailerMappingStatus &&
            this.props.retailerMappingStatus === "UPDATE_FAILED" &&
            this.props.retailerMappingError &&
            this.props.retailerMapping.length !== this.state.mappingData.size
        ) {
            this.setState({
                mappingData: new List(this.props.retailerMapping.map(_ => {
                    let error = new Map(this.props.retailerMappingError).find((__, k) => (k.split("|")[0] && k.split("|")[0].trim() == _.account_number) && (k.split("|")[1] && k.split("|")[1].trim() == _.dealer_gstin));
                    return {
                        dealer_gstin: _.dealer_gstin,
                        account_type: _.account_type,
                        account_holder_name: _.account_holder_name,
                        account_number: _.account_number,
                        ifsc_code: _.ifsc_code,
                        error
                    };
                })),
                showErrorTable: true
            });
        }
    }

    fieldGenerator = fieldGenerator.bind(this.props.form);
    getErrorOnField = getErrorOnField.bind(this.props.form);
    getValidateStatus = getValidateStatus.bind(this.props.form);
    onValuesSubmit = e => {
        e.preventDefault();
        if (!this.props.loading) {
            this.props.form.validateFields((err, { users, bank_details, ...restValues }) => {
                let spocUsers = users && new Map(users).map((_) => {
                    const values = Object.keys(_).filter(__ => !!_[__]);
                    if(values && values.length) {
                        return _;
                    }
                    return null;
                }).filter(_ => !!_);
                const is_bank_details = bank_details && Object.keys(bank_details)?.filter(_ => !!bank_details[_]);
                if(bank_details && bank_details.account_number && bank_details.re_enter_account_number && this.props.form.getFieldsValue().bank_details.account_number!==this.props.form.getFieldsValue().bank_details?.re_enter_account_number){
                    message.error("Please re-enter the same account number");
                }
                if (bank_details && bank_details.account_number ? bank_details.re_enter_account_number && this.props.form.getFieldsValue().bank_details.account_number===this.props.form.getFieldsValue().bank_details?.re_enter_account_number && !err : !err) {
                    let removed_users = this.state.userConfigs.filter(_ =>
                        this.state.removed_users.includes(_.id)
                    );
                    let filteredUsers = removed_users
                        .map(_ => {
                            const val = _;
                            return {
                                id: `${val.id}|user`,
                                ...val
                            };
                        })
                        .toMap()
                        .mapEntries(([, v]) => [v.id, v])
                        .toJS();
                    this.props.onValuesSubmit({
                        ...restValues
                    }, 
                    spocUsers && spocUsers.size ? spocUsers.map((_, id) => ({
                        ..._,
                        id: (
                            this.state.userConfigs.find(
                                _ =>
                                    _.id ===
                                          Number(
                                              id.split(
                                                  "|"
                                              )[0]
                                          ) ||
                                      _.id === Number(id)
                            ) || { id: null }
                        ).id
                    })).toList().toArray() : null,
                    is_bank_details && is_bank_details.length && bank_details.account_number && {
                        ...bank_details,
                        id: this.props.bankDetails && this.props.bankDetails.id,
                        company: this.props.companyValues && this.props.companyValues.id,
                        ifsc: bank_details.ifsc && bank_details.ifsc.id,
                        bank_branch: bank_details.bank_branch && bank_details.bank_branch.id,
                    }, filteredUsers);
                }
            });
        }
    };

    formatData = (data) => {
        if(data) {
            return data.map(_ => {
                return {
                    ..._,
                    name: `${_.name} - ${_.gst_state} (${_.gstin})`
                };
            });
        }
        return data;
    }

    formatIndustryData = (data) => {
        const myArrayFiltered = data.filter((el) => {
            return this.props.form.getFieldsValue().all_industries.some((f) => {
                return f.id === el.id && f.name === el.name;
            });
        });
        return myArrayFiltered;
    }

    formatProgramAnchorData = () => {
        let data = this.props.form.getFieldsValue().all_anchors;
        const filteredProgAnchorData = [];
        data && data.map(item => {
            if(item?.industry?.id === this.props.form.getFieldsValue().industry.id){
                filteredProgAnchorData.push(item);
            }
        });
        return filteredProgAnchorData;
    }

    formFields = getFormFields({
        gstinValidator: this.props.gstinValidator,
        getChannelProducts: this.props.fetchChannelProducts,
        salesManagers: this.props.fetchSalesManagers,
        distributorManagers: this.props.fetchDistributorManagers,
        vpSalesManagers: this.props.fetchVpSalesManagers,
        drmManagers: this.props.fetchDRMManagers,
        allFieldsDisabled: this.props.loading,
        gstinDisabled: this.props.gstinDisabled,
        formatSellerData: this.formatData,
        fetchIndustryTypes: this.props.fetchIndustryTypes
    });

    distFormFields = getDistFormFields({
        gstinValidator: this.props.gstinValidator,
        getChannelProducts: this.props.fetchChannelProducts,
        salesManagers: this.props.fetchSalesManagers,
        distributorManagers: this.props.fetchDistributorManagers,
        vpSalesManagers: this.props.fetchVpSalesManagers,
        drmManagers: this.props.fetchDRMManagers,
        allFieldsDisabled: this.props.loading,
        gstinDisabled: this.props.gstinDisabled,
        formatSellerData: this.formatData,
        formatIndustryData: this.formatIndustryData,
        formatProgramAnchorData: this.formatProgramAnchorData,
        fetchIndustryTypes: this.props.fetchIndustryTypes
    });

    bankFormFields = getBankFields(this.props.loading);
    userFormFields = !this.props.profile.getIn(["data", "is_d2r_db"]) ? getUserDetails(this.props.loading).filter((_ , key) => key != 'is_d2r_guarantor') :  getUserDetails(this.props.loading);
    ifscLookup = (str) =>
        CompanyService.ifscLookup(str).then(({ data }) => ({
            data: data.results
        }));

    getValueFromObject = bankBranch => {
        if (!bankBranch) {
            return null;
        }
        return bankBranch.name;
    };
    setBankBranchName = branch => {
        this.props.form.setFieldsValue({
            "bank_details.bank_branch": branch
        });
    };

    toggleFormGrid = (action = "ADD", id) => {
        const { userIds } = this.state;
        let removed_users = this.state.removed_users;
        const newID = shortID();
        switch (action) {
        case "ADD":
            this.setState({
                userIds: userIds.set(newID, newID)
            });
            break;
        case "REMOVE": {
            removed_users.push(id);
            this.setState({
                userIds: userIds.filter(_ => _ !== id),
                removed_users: removed_users
            });
            break;
        }
        default:
        }
    };


    getCompanyForm = () => (
        <Card className={"no-shadow border-none"}>
            <div className={"d-flex flex-row align-items-center flex-wrap w-100"}>
                {this.formFields.map((field, fieldName) => {
                    const profileType = this.props.profile &&
                        this.props.profile.getIn(["data", "type"]);
                    const companyType =
                        profileType != null ? Number(profileType) : null;

                    if (
                        (fieldName === "parent_industry" ||
                            fieldName === "industry_head" ||
                            fieldName === "zonal_head") &&
                        companyType === 3
                    ) {
                        return null;
                    }

                    field = { ...field };
                    field.disabled = this.props.disableFields;

                    if (
                        fieldName === "vp_sales" &&
                        (companyType === 1 || companyType === 2)
                    ) {
                        field.type = INPUT_TYPES.TEXT;
                        field.label = "VP Sales";
                        field.className = "form-field fb-30 mr-3perc";
                        field.category = 1;
                        field.disabled = true;
                        delete field.labelProp;
                        delete field.showSearch;
                        delete field.onSearch;
                        delete field.getOptionsOnFocus;
                        delete field.filterOption;
                    }

                    if (
                        companyType === 1 ||
                        companyType === 2
                    ) {
                        if (
                            fieldName === "vp_sales" ||
                            fieldName === "zonal_head" ||
                            fieldName === "parent_industry" ||
                            fieldName === "industry_head"
                        ) {
                            field.disabled = true;
                        }
                    }
                    if(fieldName === "corporation_type" && !this.props.userGroups.includes(7)) {
                        field.disabled = true;
                    }
                    if(fieldName === "db_tagging") {
                        field.disabled = true;
                        if (this.props.profile.get("data").type === 3) {
                            return null; 
                        }
                    }
                    if(fieldName === "gstin") {
                        field.disabled = true;
                    }
                    if (fieldName === "name") {
                        field.disabled = true;
                    }
                    return this.fieldGenerator(
                        fieldName,
                        field,
                        this.getValidateStatus(fieldName)
                    )();
                }).toList()}
            </div>
        </Card>
    );

    getDistributorCompanyForm = () => (
        <Card className={"no-shadow border-none"}>
            <div className={"d-flex flex-row align-items-center flex-wrap w-100"}>
                {this.distFormFields.map((field, fieldName) => {
                    field = { ...field };
                    const hierarchyLocked = [
                        "vp_sales",
                        "zonal_head",
                        "parent_industry",
                        "industry_head"
                    ].includes(fieldName);
                    field.disabled = hierarchyLocked
                        ? true
                        : this.props.disableFields;
                    if (fieldName === "gstin") {
                        field.disabled = true;
                    }
                    if (fieldName === "db_tagging") {
                        field.disabled = true;
                    }
                    if (fieldName === "name") {
                        field.disabled = true;
                    }
                    if(fieldName === "program_anchor"){
                        if(this.props.form.getFieldsValue().industry?.id && this.props.form.getFieldsValue().all_industries && this.props.form.getFieldsValue().all_industries.length > 0 && this.props.form.getFieldsValue().all_anchors && this.props.form.getFieldsValue().all_anchors.length > 0){
                            field.disabled = false;
                            field.rules = [
                                {
                                    required: true,
                                    message: "Field Required"
                                }
                            ];
                        }else{
                            field.disabled = true;
                            field.rules = [
                                {
                                    required: true,
                                    message: "Field Required"
                                }
                            ];
                        }
                    }
                    if(fieldName === "distributor_service_manager"){
                        let  fieldOptions= this.state.distributorManagersData && this.state.distributorManagersData.size ? [{id: 0, full_name: "None"} , ...this.state.distributorManagersData] : new List([]);
                        field.options = this.state.distributorManagersData && this.state.distributorManagersData.size && fieldOptions;
                        field.disabled = true;
                    }
                    if(fieldName === "all_anchors"){
                        if(this.props.form.getFieldsValue().all_industries && this.props.form.getFieldsValue().all_industries.length > 0){
                            field.disabled = false;
                            field.onSearch = this.fetchAnchorList;
                            field.onChange = this.setAllAnchorList;
                            field.rules = [
                                {
                                    required: true,
                                    message: "Field Required"
                                }
                            ];
                        }else {
                            field.disabled = true;
                            field.rules = [
                                {
                                    required: true,
                                    message: "Field Required"
                                }
                            ];
                        }
                    }
                    if(fieldName === "industry"){
                        if(this.props.form.getFieldsValue().all_industries && this.props.form.getFieldsValue().all_industries.length > 0){
                            field.disabled = false;
                            field.onChange = this.setIndustryAndAnchor;
                            field.rules = [
                                {
                                    required: true,
                                    message: "Field Required"
                                }
                            ];
                        }else{
                            field.disabled = true;
                            field.rules = [
                                {
                                    required: true,
                                    message: "Field Required"
                                }
                            ];
                        }
                    }
                    if(fieldName === "all_industries"){
                        field.onChange = this.setAllIndustries;
                        field.rules = [
                            {
                                required: true,
                                message: "Field Required"
                            }
                        ];
                    }
                    
                    return this.fieldGenerator(
                        fieldName,
                        field,
                        this.getValidateStatus(fieldName)
                    )();
                }).toList()}
            </div>
        </Card>
    );

    setAllIndustries = (data) => {
        this.props.form.setFieldsValue({
            "all_industries": data,
            "all_anchors": [],
            "industry": null,
            "program_anchor": null
        });
    }

    setAllAnchorList = (data) => {
        this.props.form.setFieldsValue({
            "all_anchors": data,
            "industry": null,
            "program_anchor": null
        });
    }

    setIndustryAndAnchor = industry => {
        this.props.form.setFieldsValue({
            "industry": industry,
            "program_anchor": []
        });
    }

    fetchAnchorList = (str) => {
        let industryIdList = [];
        this.props.form.getFieldsValue().all_industries.map((item) => {
            industryIdList.push(item.id);
        });
        return CompanyService.getCompanyProfilesFilterList(COMPANY_TYPES.get("Anchor"), str, industryIdList);
    }

    fetchProgramAnchor = (str) => {
        return CompanyService.getCompanyProfiles(COMPANY_TYPES.get("Anchor"), str);
    }

    getUserDetailsForm = () => {
        return(
        <Card className={"no-shadow border-none"}>
            <div>
                {
                    this.state.userIds && this.state.userIds.size ?
                        this.state.userIds.map(_ => (
                            <div 
                                key={`User-${_}`}
                                className={"d-flex flex-row align-items-center flex-wrap w-100 mb-10"}
                            >
                                {this.userFormFields.map((field, fieldName) => {
                                    field.disabled = this.props.disableFields;
                                    if(fieldName === "phone") {
                                        let firstName = this.props.form.getFieldValue(`users.${_}|user.first_name`);
                                        if(firstName) {
                                            field.rules = [
                                                {
                                                    required: true,
                                                    message: "Field is Required"
                                                }
                                            ];
                                        } else {
                                            field.rules = [];
                                        }
                                    }
                                    if(fieldName === "email") {
                                        let firstName = this.props.form.getFieldValue(`users.${_}|user.first_name`);
                                        if(firstName) {
                                            field.rules = [
                                                {
                                                    required: true,
                                                    message: "Field is Required"
                                                }
                                            ];
                                        } else {
                                            field.rules = [];
                                        }
                                    }
                                    // if(((fieldName ==="email" || fieldName ==="phone") &&  ((_ + "").length<6))){
                                    //     field.disabled = true;
                                    // }
                                    // else{
                                    //     field.disabled = false;
                                    // }
                                    return this.fieldGenerator(
                                        `users.${_}|user.${fieldName}`,
                                        field,
                                        this.getValidateStatus(fieldName)
                                    )();
                                }).toList()}
                                <Form.Item className={cx("m-0 form-btn")}>
                                    <Button
                                        icon={"minus"}
                                        disabled={this.state.userIds && this.state.userIds.size < 2}
                                        onClick={this.toggleFormGrid.bind(
                                            null,
                                            "REMOVE",
                                            _
                                        )}
                                    >{"Remove"}</Button>
                                </Form.Item>
                            </div>
                        )).toList()
                        :null
                }
            </div>
        </Card>
    )};

    getBankDetailsForm = () => (
        <div className={"d-flex flex-row align-items-center flex-wrap w-100"}>
            {this.bankFormFields.map((field, fieldName) => {
                if(fieldName === "bank_details.account_type") {
                    field.disabled = true;
                } else {
                    field.disabled = this.props.disableFields;
                }
                if(fieldName === "bank_details.ifsc") {
                    field.onSearch = this.ifscLookup;
                    field.onSelect = this.setBankBranchName;
                }
                if(fieldName === "bank_details.bank_branch") {
                    field.getValueFromObject = this.getValueFromObject;
                }
                if(fieldName === "bank_details.account_number" || fieldName === "bank_details.re_enter_account_number"){
                    field.disableFieldCopy =  (e) => {
                        e.preventDefault();
                        return true;
                    };
                }
                if(fieldName === "bank_details.account_number"  || fieldName === "bank_details.re_enter_account_number"){
                    field.autocomplete="new-password";
                    field.disableFieldPaste =  (e) => {
                        e.preventDefault();
                        return true;
                    };
                }
                return this.fieldGenerator(
                    fieldName,
                    field,
                    this.getValidateStatus(fieldName)
                )();
            }).toList()}
        </div>
    );

    getBankListing = () => (
        <div>
            {
                this.state.bankListData && this.state.bankListData.map((_, index) => {
                    return (
                        <React.Fragment key={_.id}>
                            <h3 key={index} className={"ant-form-item-label"}>Bank Account Detail: {index + 1}</h3>
                            <div className={"d-flex flex-row flex-wrap w-100"}>
                                <div className={"ant-row ant-form-item form-field fb-30 mr-3perc"}>
                                    <div className={"ant-col ant-col-24 ant-form-item-label"}>
                                        <label>Account Type</label><br />
                                    </div>
                                    <Select defaultValue={_.account_type.toString()} disabled={true}>
                                        <Option value="1">Saving</Option>
                                        <Option value="2">Current</Option>
                                        <Option value="3">OverDraft</Option>
                                        <Option value="4">CashCredit</Option>
                                        <Option value="5">Disbursal & Withhold</Option>
                                        <Option value="6">Loan</Option>
                                        <Option value="7">Withhold</Option>
                                        <Option value="8">Disbursal</Option>
                                    </Select>
                                </div>
                                <div className={"ant-row ant-form-item form-field fb-30 mr-3perc"}>
                                    <div className={"ant-col ant-col-24 ant-form-item-label"}>
                                        <label>Account Holder Name</label><br />
                                    </div>
                                    <input className={"ant-input"} value={_.account_holder_name} readOnly /><br /><br />
                                </div>
                                <div className={"ant-row ant-form-item form-field fb-30 mr-3perc"}>
                                    <div className={"ant-col ant-col-24 ant-form-item-label"}>
                                        <label>Account Number</label><br />
                                    </div>
                                    <input className={"ant-input"} value={_.account_number} readOnly /><br /><br />
                                </div>
                                <div className={"ant-row ant-form-item form-field fb-30 mr-3perc"}>
                                    <div className={"ant-col ant-col-24 ant-form-item-label"}>
                                        <label>Bank IFSC Code</label><br />
                                    </div>
                                    <input className={"ant-input"} value={_.bank_branch.ifsc_code} readOnly /><br /><br />
                                </div>
                                <div className={"ant-row ant-form-item form-field fb-30 mr-3perc"}>
                                    <div className={"ant-col ant-col-24 ant-form-item-label"}>
                                        <label>Bank Branch</label><br />
                                    </div>
                                    <input className={"ant-input"} value={_.bank_branch.name} readOnly /><br /><br />
                                </div>
                                <div className={"ant-row ant-form-item form-field fb-30 mr-3perc"}>
                                    <Button
                                        style={{marginTop: "27px"}}
                                        icon={"minus"}
                                        onClick={() => this.setState({bankRemoveConf: true, bankDeleteData: _})}
                                    >Remove</Button>
                                </div>
                            </div>
                        </React.Fragment>
                    );
                })
            }
        </div>
    );

    deleteBank(_) {
        if(_){
            CompanyLosService.deleteCompanyBank(_.id).then(() => {
                this.setState({
                    bankRemoveConf: false,
                    bankDeleteData: null
                });
                this.props.fetchCompanyLosProfile(this.props.companyId);
                message.success("Bank account removed successfully");
            }).catch((err) => {
                console.error(err);
            });
        }
    }


    downloadSampleSheet = () => {
        return import(/* webpackChunkName: "xlsx" */ "xlsx")
            .then(_ => _.default)
            .then(async XLSX => {
                let cols = RETAILER_WISE_MAPPING_COLS.filter(_ => _.dataIndex !== "dealer_name").map(_ => _.dataIndex).toList();
                let ws = await XLSX.utils.json_to_sheet([], {header: cols.toArray()});
                let wb = await XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
                const wbout = XLSX.writeFile(wb, "sample-retailer_wise_new_mapping.xlsx");
                return wbout;
            });
    }

    downloadErrorFile = () => {
        return import(/* webpackChunkName: "xlsx" */ "xlsx")
            .then(_ => _.default)
            .then(async XLSX => {
                let cols = RETAILER_WISE_MAPPING_COLS.filter(_ => _.dataIndex !== "dealer_name").map(_ => _.dataIndex).toList();
                let ws = await XLSX.utils.json_to_sheet(this.state.mappingData.toArray(), {header: [...cols.toArray(), "error"]});
                let wb = await XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "SheetJS");
                const wbout = XLSX.writeFile(wb, "sample-retailer_wise_new_mapping.xlsx");
                return wbout;
            });
    }

    readFile = async (file) => {
        let data = await readSpreadsheet(file);
        data = new List(data);
        this.setState({
            newMappingData: data,
            showDrawer: true,
            showNewMapping: true,
            showErrorTable: false
        });
    };

    onSelectingMapping = (ids) => {
        this.filteredData = this.state.mappingData.filter(_ => ids.toJS().includes(_.id));
    }

    addSingleRow = () => {
        this.setState({
            addSingle: true,
            showDrawer: true,
            showErrorTable: false
        });
    }

    editRetailerMappings = () => {
        this.setState({
            showDrawer: true,
            editing: true,
            showErrorTable: false
        });
    }

    getRetailerWiseMapping = () => {
        if(!this.state.mappingData || !this.state.mappingData.size) {
            return (
                <Card>
                    <div className={"d-flex justify-content-space-between align-items-center"}>
                        <div className={"fb-50 d-flex flex-row align-items-center"}>
                            <div className={"mr-5"}>
                                <img src={Clipboard} width={"120px"} />
                            </div>
                            <div style={{"color": "#333333 solid"}} className={"d-flex flex-column"}>
                                <span style={{
                                    fontSize: "20px",
                                    fontWeight: "600"
                                }}>{"No Data Found"}</span>
                                <span style={{
                                    fontSize: "12px",
                                    fontStyle: "light"
                                }}>
                                    {"Retailer wise mapping is not created yet, please create your first mapping"}
                                </span>
                            </div>
                        </div>
                        <div>
                            <Button className={"mr-5"} type={"primary"} icon={"download"} onClick={this.downloadSampleSheet}>
                                {"Download Sample Sheet"}
                            </Button>
                            <Upload
                                beforeUpload={(file) => this.readFile(file)}
                                action={null}
                            >
                                <Button type={"primary"} icon={"upload"}>
                                    {"Upload Data Sheet"}
                                </Button>
                            </Upload>
                        </div>
                    </div>
                </Card>
            );
        }

        return (
            <React.Fragment>
                <div className={"mt-5 mb-10 d-flex flex-row justify-content-space-between"}>
                    <div>
                        <Button className={"mr-5"} type={"default"} icon={"plus"} onClick={this.addSingleRow}>
                            {"Add Single Entry"}
                        </Button>
                        {!this.state.showErrorTable ?<Button type={"primary"} icon={"edit"} onClick={this.editRetailerMappings}>
                            {"Edit Details"}
                        </Button> : null}
                    </div>
                    <div>
                        {!this.state.showErrorTable ?
                            <React.Fragment>
                                <Button className={"mr-5"} type={"primary"} icon={"download"} onClick={this.downloadSampleSheet}>
                                    {"Download Sample Sheet"}
                                </Button>
                                <Upload
                                    beforeUpload={(file) => this.readFile(file)}
                                    action={null}
                                >
                                    <Button type={"primary"} icon={"upload"}>
                                        {"Upload Data Sheet"}
                                    </Button>
                                </Upload>
                            </React.Fragment> 
                            :
                            <React.Fragment>
                                <span className={"mr-5"}>
                                    {new Map(this.props.retailerMappingError).size} Errors in your File
                                </span>
                                <Button type={"primary"} className={"mr-5"} icon={"download"} onClick={this.downloadErrorFile}>
                                    {"Download Error File"}
                                </Button>
                                <Upload
                                    beforeUpload={(file) => this.readFile(file)}
                                    action={null}
                                >
                                    <Button type={"primary"} icon={"upload"}>
                                        {"Upload Data Sheet"}
                                    </Button>
                                </Upload>
                            </React.Fragment>
                        }
                        
                    </div>
                </div>
                <Card className={"padless-card"}>
                    <Table
                        columns={!this.state.showErrorTable ? RETAILER_WISE_MAPPING_COLS : EDITABLE_RETAILER_WISE_MAPPING_COLS.map(_ => {
                            let row = _;
                            row.editable = false;
                            return row;
                        })}
                        keyFilter={"id"}
                        onRowSelection={this.onSelectingMapping}
                        data={this.state.mappingData}
                        noRowSelection={false}
                        showTopSection={false}
                    />
                </Card>
            </React.Fragment>
        );
    }

    onChangeTab = (val) => {
        this.setState({
            activeTab: val,
            showErrorTable: false
        });
    }

    getTabs() {
        return (
            <Tabs
                tabBarStyle={{backgroundColor: "#fff"}}
                className={"fixed-tabs"}
                animated={false}
                activeKey={this.state.activeTab}
                onChange={this.onChangeTab}
            >
                <TabPane className={"fixTab"} key={"default-account"} tab={"Default Account"}>
                    <Card className={"no-shadow border-none"}>
                        {
                            this.state.bankListData && this.state.bankListData.length !== 0 ? <React.Fragment>
                                {this.getBankListing()}
                                <h3 className={"ant-form-item-label"}>Add Bank +</h3>
                            </React.Fragment>:null
                        }
                        {this.getBankDetailsForm()}
                    </Card>
                </TabPane>
                <TabPane className={"fixTab"} key={"retailer-wise-mapping"} tab={"Dealer wise mapping"}>
                    {this.getRetailerWiseMapping()}
                </TabPane>
            </Tabs>
        );
    }

    showDataVisibility = () => {
        return (
            <Table
                columns={RETAILER_WISE_MAPPING_COLS}
                data={this.state.newMappingData}
                // keyFilter={"dealer_gstin"}
                noRowSelection={true}
                showTopSection={false}
            />
        );
    }

    handleSaveMultipleEntries = (record, values) => {
        if(record.id && this.state.newMappingData.size) {
            let data = this.state.newMappingData.toJS();
            data = data.map(_ => {
                if(_.id == record.id) {
                    return {
                        ...values,
                        id: record.id
                    };
                }
                return _;
            });
            this.setState({
                newMappingData: data
            });
        }
        this.setState({
            newMappingData: new List([
                {
                    ...values,
                    id: record.id || null
                }
            ]),
            showErrorTable: false
        });
    }

    singleRowTable = () => {
        return (
            <Table
                columns={EDITABLE_RETAILER_WISE_MAPPING_COLS.map(({ onCellGenerator, editable, ...restColFields}) => {
                    if (!onCellGenerator || !editable) {
                        return { ...restColFields };
                    }
                    return {
                        onCell: onCellGenerator(
                            this.handleSaveMultipleEntries,
                            restColFields
                        ),
                        ...restColFields
                    };
                })}
                data={new List([{}])}
                editable={true}
                // keyFilter={"dealer_gstin"}
                noRowSelection={true}
                showTopSection={false}
            />
        );
    }

    editingTable = () => {
        return (
            <Table
                columns={EDITABLE_RETAILER_WISE_MAPPING_COLS.map(({ onCellGenerator, editable, ...restColFields}) => {
                    if (!onCellGenerator || !editable) {
                        return { ...restColFields };
                    }
                    return {
                        onCell: onCellGenerator(
                            this.handleSaveMultipleEntries,
                            restColFields
                        ),
                        ...restColFields
                    };
                })}
                data={this.filteredData}
                // keyFilter={"dealer_gstin"}
                editable={true}
                noRowSelection={true}
                showTopSection={false}
            />
        );
    }

    getDrawerContent = () => {
        if(this.state.editing) {
            return this.editingTable();
        } else if(this.state.addSingle) {
            return this.singleRowTable();
        } else if(this.state.showNewMapping) {
            return this.showDataVisibility();
        }
    }

    getTitle = () => {
        if(this.state.editing) {
            return "Update Retailer wise mapping";
        } else if(this.state.addSingle) {
            return "Add New Retailer wise mapping";
        } else if(this.state.showNewMapping) {
            return "Verify Retailer Mapping from Sheet";
        }
    }

    closeDrawer = () => {
        this.setState({
            showDrawer: false,
            editing: false,
            addSingle: false,
            showNewMapping: false,
            showErrorTable: false
        });
    }

    updateRetailerMapping = () => {
        this.props.addRetailerMapping(this.state.newMappingData.toArray().map(_ => {
            return {
                ..._,
                company_gstin: this.props.gstin
            };
        }));
        this.closeDrawer();
    }

    render() {

        return (
            <React.Fragment>
                <Drawer
                    title={
                        <div className={"w-100 d-flex justify-content-space-between"}>
                            <span>
                                {this.getTitle()}
                            </span>
                            <div>
                                <Button className={"mr-5"} type={"primary"} onClick={this.updateRetailerMapping}>Update</Button>
                                <Button type={"default"} onClick={this.closeDrawer}>Cancel</Button>
                            </div>
                        </div>
                    }
                    closable={false}
                    visible={this.state.showDrawer}
                    placement={"bottom"}
                    destroyOnClose={true}
                    onClose={this.closeDrawer}
                    
                >
                    {this.getDrawerContent()}
                </Drawer>
                <Form onSubmit={this.onValuesSubmit}>
                    <div className={"form-row align-items-center"}>    
                        <div className={"w-100 pt-10"}>
                            <h3 className={"pt-5 pb-5"}>{"Profile Details"}</h3>
                            {this.props.form.getFieldsValue().type?.toString() === "2" ? this.getDistributorCompanyForm():this.getCompanyForm()}
                            {/* {this.getCompanyForm()} */}
                        </div>
                        <div className={"w-100 pt-10"}>
                            <div className={"d-flex justify-content-space-between"}>
                                <h3 className={"pt-5 pb-5"}>{"Applicant Details"}</h3>
                                <Form.Item className={cx("form-btn m-0")}>
                                    <Button
                                        icon={"plus"}
                                        size={"small"}
                                        disabled={this.props.isDBOnBoarding}
                                        onClick={this.toggleFormGrid.bind(null, "ADD")}>
                                        Add User
                                    </Button>
                                </Form.Item>
                            </div>
                            
                            {this.getUserDetailsForm()}
                        </div>
                        {/* <div className={"w-100 pt-10"}>
                            <h3 className={"pt-5 pb-5"}>{"Company Bank Account Details"}</h3>
                            {this.getTabs()}
                        </div> */}
                    </div>
                    {!this.props.disableFields ? (
                        <div className={"d-flex flex-row-reverse"}>
                            <Form.Item>
                                <Button type="primary" className={"mt-10"} htmlType={"submit"}>{this.props.loading ? <Icon type={"loading"} /> : null}Submit</Button>
                            </Form.Item>
                        </div>
                    ) : null}
                </Form>
                <Modal title="Confirm Delete" visible={this.state.bankRemoveConf} onOk={() => this.deleteBank(this.state.bankDeleteData)} onCancel={() => this.setState({bankRemoveConf: false})} okText="Yes" cancelText="No">
                    <p>Are you sure?</p>
                </Modal>
            </React.Fragment>
        );
    }
}

CompanyCreateForm.propTypes = {
    form: PropTypes.object,
    companyValues: PropTypes.object,
    onValuesSubmit: PropTypes.func,
    gstinValidator: PropTypes.object,
    gstinDisabled: PropTypes.bool,
    fetchChannelProducts: PropTypes.func,
    salesManagers: PropTypes.array,
    loading: PropTypes.bool,
    spocValues: PropTypes.array,
    bankDetails: PropTypes.object,
    disableFields: PropTypes.bool,
    fetchSalesManagers: PropTypes.func,
    fetchDRMManagers: PropTypes.func,
    detail: PropTypes.object,
    addRetailerMapping: PropTypes.func,
    retailerMapping: PropTypes.array,
    fetchRetailerMapping: PropTypes.func,
    gstin: PropTypes.string,
    retailerMappingStatus: PropTypes.string,
    retailerMappingError: PropTypes.object,
    fetchIndustryTypes: PropTypes.func,
    vpSalesManagers: PropTypes.array,
    fetchVpSalesManagers: PropTypes.func,
    companyId: PropTypes.string,
    fetchCompanyLosProfile : PropTypes.func,
    profile : PropTypes.object,
    fetchDistributorManagers: PropTypes.func
};

CompanyService.defaultProps = {
    salesManagers: [],
    vpSalesManagers: [],
    disableFields: false
};

export default Form.create({
    mapPropsToFields: ({ companyValues, spocValues, detail }) => {
        let company = new Map(companyValues);
        let spoc = spocValues;
        // let bank = bankDetails && bankDetails.mapEntries(([k, v]) => [`bank_details.${k}`, v]).set(
        //     "bank_details.ifsc_code",
        //     bankDetails.bank_branch
        // );
        let mergedRecord = company.merge({program_anchor: detail && detail.program_anchor, all_anchors: detail && detail.all_anchors, all_industries: detail && detail.all_industries});
        // if(bank && bank.size) {
        //     mergedRecord = mergedRecord.merge(bank);
        // }
        const corporationTypeLabel = companyValues.get("corporation_type");
        if (typeof corporationTypeLabel === 'string') {
            mergedRecord = mergedRecord.set('corporation_type', CORPORATION_TYPE.get(corporationTypeLabel));
        }
        if (spoc && spoc.size) {
            mergedRecord = mergedRecord.merge({users: spoc});
            return mergedRecord.map((value, key) => {
                if(key === "users") {
                    return value
                        .mapEntries(([, v]) => ([`${v.id}|user`, v]))
                        .map(obj =>
                            new Map(obj)
                                .map(_ => {
                                    return Form.createFormField({
                                        value: _
                                    });
                                })
                        ).toJS();
                }
                return Form.createFormField({
                    value
                });
            }).toJS();
        }
        return mergedRecord.map((value) =>
            Form.createFormField({
                value
            })
        ).toJS();
    }
})(CompanyCreateForm);